import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PhotoLightbox, PhotoThumb } from '@/components/ui/PhotoLightbox';
import type { StudentRecord, ClassSummary } from '@/types';
import { resolveUserPhoto } from '@/utils/avatars';

const UNASSIGNED = 'unassigned';

const selectClassName =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-base sm:text-sm min-h-[40px] shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30';

const classLabel = (classItem: ClassSummary) =>
  classItem.sectionName ? `${classItem.gradeLevel} - ${classItem.sectionName}` : classItem.name || classItem.gradeLevel;

const classRank = (classItem: ClassSummary) => {
  const grade = classItem.gradeLevel || '';
  if (grade === 'Nursery') return 0;
  if (grade === 'Prep') return 1;
  const match = grade.match(/Grade\s+(\d+)/i);
  if (match) return 10 + Number(match[1]);
  return 100;
};

const compareReg = (a: StudentRecord, b: StudentRecord) => {
  const aDigits = /^\d+$/.test(a.rollNumber);
  const bDigits = /^\d+$/.test(b.rollNumber);
  if (aDigits && bDigits) return Number(a.rollNumber) - Number(b.rollNumber);
  return a.rollNumber.localeCompare(b.rollNumber, undefined, { numeric: true });
};

const studentLine = (student: StudentRecord) => `${student.rollNumber}  ${student.firstName} ${student.lastName}`;

type ClassTransferPanelProps = {
  intro: string;
  students: StudentRecord[];
  classes: ClassSummary[];
  onConfirm: (studentIds: string[], toClassId: string | null) => Promise<unknown>;
  lockedFromClassId?: string;
  allowUnassigned?: boolean;
  destinationStudents?: StudentRecord[];
  onToClassChange?: (classId: string) => void;
};

type ClassChipRowProps = {
  classList: ClassSummary[];
  value: string;
  onChange: (next: string) => void;
  allowUnassigned?: boolean;
};

function ClassChipRow({ classList, value, onChange, allowUnassigned = true }: ClassChipRowProps) {
  return (
    <div className="mt-2 flex max-h-28 flex-wrap gap-1.5 overflow-y-auto">
      {classList.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onChange(item.id)}
          aria-pressed={value === item.id}
          className={clsx(
            'rounded-full border px-2.5 py-1 text-xs font-medium min-h-[30px] flex items-center justify-center transition-colors',
            value === item.id
              ? 'border-brand bg-brand text-white'
              : 'border-slate-200 bg-white text-slate-700 hover:border-brand/40',
          )}
        >
          {classLabel(item)}
        </button>
      ))}
      {allowUnassigned ? (
        <button
          type="button"
          onClick={() => onChange(UNASSIGNED)}
          aria-pressed={value === UNASSIGNED}
          className={clsx(
            'rounded-full border px-2.5 py-1 text-xs font-medium min-h-[30px] flex items-center justify-center transition-colors',
            value === UNASSIGNED
              ? 'border-brand bg-brand text-white'
              : 'border-slate-200 bg-white text-slate-700 hover:border-brand/40',
          )}
        >
          Unassigned
        </button>
      ) : null}
    </div>
  );
}

export function ClassTransferPanel({
  intro,
  students,
  classes,
  onConfirm,
  lockedFromClassId,
  allowUnassigned = true,
  destinationStudents = [],
  onToClassChange,
}: ClassTransferPanelProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const fromLocked = Boolean(lockedFromClassId);
  const [fromClassId, setFromClassId] = useState(lockedFromClassId || '');
  const [toClassId, setToClassId] = useState('');
  const [fromSelected, setFromSelected] = useState<string[]>([]);
  const [stagedSelected, setStagedSelected] = useState<string[]>([]);
  const [stagedIds, setStagedIds] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [pendingFrom, setPendingFrom] = useState<string | null>(null);
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  useEffect(() => {
    if (lockedFromClassId) setFromClassId(lockedFromClassId);
  }, [lockedFromClassId]);

  const setDestination = (next: string) => {
    setToClassId(next);
    setStagedSelected([]);
    onToClassChange?.(next);
  };

  const classList = useMemo(
    () => [...(Array.isArray(classes) ? classes : [])].sort((a, b) => classRank(a) - classRank(b) || classLabel(a).localeCompare(classLabel(b))),
    [classes],
  );

  const studentList = useMemo(() => {
    const byId = new Map<string, StudentRecord>();
    for (const student of [...(Array.isArray(students) ? students : []), ...destinationStudents]) {
      byId.set(student.id, student);
    }
    return [...byId.values()];
  }, [students, destinationStudents]);

  const activeStudents = studentList.filter((student) => student.status !== 'Inactive');

  const fromLabel = fromClassId === UNASSIGNED ? 'Unassigned' : classList.find((item) => item.id === fromClassId)?.name || 'From';
  const toLabel = toClassId === UNASSIGNED ? 'Unassigned' : classList.find((item) => item.id === toClassId)?.name || 'To';

  const matchesClass = (student: StudentRecord, classId: string) => {
    if (classId === UNASSIGNED) return !student.classId;
    return student.classId === classId;
  };

  const fromStudents = useMemo(
    () =>
      activeStudents
        .filter((student) => fromClassId && matchesClass(student, fromClassId) && !stagedIds.includes(student.id))
        .sort(compareReg),
    [activeStudents, fromClassId, stagedIds],
  );

  const alreadyThere = useMemo(
    () =>
      activeStudents
        .filter((student) => toClassId && matchesClass(student, toClassId) && !stagedIds.includes(student.id))
        .sort(compareReg),
    [activeStudents, toClassId, stagedIds],
  );

  const willJoin = useMemo(
    () => activeStudents.filter((student) => stagedIds.includes(student.id)).sort(compareReg),
    [activeStudents, stagedIds],
  );

  const stagedCount = stagedIds.length;
  const sameClass = Boolean(fromClassId && toClassId && fromClassId === toClassId);

  const confirmMutation = useMutation({
    mutationFn: () => onConfirm(stagedIds, toClassId === UNASSIGNED ? null : toClassId),
    onSuccess: () => {
      setStagedIds([]);
      setFromSelected([]);
      setStagedSelected([]);
      setConfirmOpen(false);
    },
    onError: () => alert('Unable to confirm those moves.'),
  });

  const discardStaged = () => {
    setStagedIds([]);
    setFromSelected([]);
    setStagedSelected([]);
    setDiscardOpen(false);
    if (pendingFrom !== null) {
      setFromClassId(pendingFrom);
      setPendingFrom(null);
    }
    if (pendingHref) {
      const href = pendingHref;
      setPendingHref(null);
      navigate(href);
    }
  };

  const requestFromChange = (next: string) => {
    if (fromLocked || next === fromClassId) return;
    if (stagedCount) {
      setPendingFrom(next);
      setPendingHref(null);
      setDiscardOpen(true);
      return;
    }
    setFromClassId(next);
    setFromSelected([]);
  };

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!stagedIds.length) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [stagedIds.length]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!stagedIds.length) return;
      const anchor = (event.target as HTMLElement | null)?.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#')) return;
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === location.pathname) return;
      event.preventDefault();
      event.stopPropagation();
      setPendingHref(url.pathname + url.search);
      setPendingFrom(null);
      setDiscardOpen(true);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [stagedIds.length, location.pathname]);

  const toggleId = (list: string[], id: string) =>
    list.includes(id) ? list.filter((item) => item !== id) : [...list, id];

  const moveAcross = () => {
    if (!fromClassId || !toClassId || sameClass) return;
    const moving = fromSelected.filter((id) => fromStudents.some((student) => student.id === id));
    if (!moving.length) return;
    setStagedIds((current) => [...current, ...moving.filter((id) => !current.includes(id))]);
    setFromSelected([]);
  };

  const moveBack = () => {
    if (!stagedSelected.length) return;
    setStagedIds((current) => current.filter((id) => !stagedSelected.includes(id)));
    setStagedSelected([]);
  };

  const openConfirm = () => {
    if (!stagedCount || !fromClassId || !toClassId || sameClass) return;
    if (!allowUnassigned && toClassId === UNASSIGNED) return;
    setConfirmOpen(true);
  };

  return (
    <>
      <Card title="Stage moves, then confirm once">
        <p className="mb-4 text-sm text-slate-600">{intro}</p>
        <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-start">
          <section className="rounded-lg border border-slate-200 p-4">
            <h4 className="mb-2 text-sm font-semibold text-slate-800">From class</h4>
            <select
              className={selectClassName}
              aria-label="From class"
              value={fromClassId}
              disabled={fromLocked}
              onChange={(event) => requestFromChange(event.target.value)}
            >
              {fromLocked ? (
                <option value={fromClassId}>{classList.find((item) => item.id === fromClassId) ? classLabel(classList.find((item) => item.id === fromClassId)!) : fromLabel}</option>
              ) : (
                <>
                  <option value="">Select class</option>
                  {classList.map((item) => (
                    <option key={item.id} value={item.id}>
                      {classLabel(item)}
                    </option>
                  ))}
                  {allowUnassigned ? <option value={UNASSIGNED}>Unassigned</option> : null}
                </>
              )}
            </select>
            {!fromLocked ? (
              <ClassChipRow classList={classList} value={fromClassId} onChange={requestFromChange} allowUnassigned={allowUnassigned} />
            ) : null}
            <ul className="mt-3 max-h-[28rem] space-y-1 overflow-y-auto">
              {fromStudents.map((student) => (
                <li key={student.id} className="flex items-center gap-2 rounded px-1 hover:bg-slate-50">
                  <PhotoThumb src={resolveUserPhoto(student.photo, 'student')} onClick={() => setPreviewSrc(resolveUserPhoto(student.photo, 'student'))} />
                  <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      checked={fromSelected.includes(student.id)}
                      onChange={() => setFromSelected((current) => toggleId(current, student.id))}
                    />
                    <span className="truncate">{studentLine(student)}</span>
                  </label>
                </li>
              ))}
              {fromClassId && !fromStudents.length ? (
                <li className="px-2 py-6 text-center text-sm text-slate-500">
                  {fromLocked ? 'No students in this class.' : 'No students in this class. Pick another class.'}
                </li>
              ) : null}
            </ul>
          </section>

          <div className="flex sm:flex-col items-center justify-center gap-2 py-3 sm:py-6">
            <Button variant="secondary" className="w-full sm:w-auto min-h-[44px]" onClick={moveAcross} disabled={!fromSelected.length || !toClassId || sameClass}>
              Move
            </Button>
            <Button variant="ghost" className="w-full sm:w-auto min-h-[44px]" onClick={moveBack} disabled={!stagedSelected.length}>
              Move back
            </Button>
          </div>

          <section className="rounded-lg border border-slate-200 p-4">
            <h4 className="mb-2 text-sm font-semibold text-slate-800">To class</h4>
            <select
              className={selectClassName}
              aria-label="To class"
              value={toClassId}
              onChange={(event) => setDestination(event.target.value)}
            >
              <option value="">Select class</option>
              {classList.map((item) => (
                <option key={item.id} value={item.id}>
                  {classLabel(item)}
                </option>
              ))}
              {allowUnassigned ? <option value={UNASSIGNED}>Unassigned</option> : null}
            </select>
            <ClassChipRow classList={classList} value={toClassId} onChange={setDestination} allowUnassigned={allowUnassigned} />
            {sameClass ? <p className="mt-2 text-xs text-rose-600">Pick a different destination.</p> : null}
            <div className="mt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Already there</p>
              <ul className="mt-1 max-h-48 space-y-1 overflow-y-auto">
                {alreadyThere.map((student) => (
                  <li key={student.id} className="flex items-center gap-2 px-1 py-1 text-sm text-slate-700">
                    <PhotoThumb src={resolveUserPhoto(student.photo, 'student')} onClick={() => setPreviewSrc(resolveUserPhoto(student.photo, 'student'))} />
                    <span className="truncate">{studentLine(student)}</span>
                  </li>
                ))}
                {toClassId && !alreadyThere.length ? <li className="px-2 py-2 text-sm text-slate-400">None yet.</li> : null}
              </ul>
            </div>
            <div className="mt-4 border-t border-dashed border-slate-200 pt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Will join</p>
              <ul className="mt-1 max-h-48 space-y-1 overflow-y-auto">
                {willJoin.map((student) => (
                  <li key={student.id} className="flex items-center gap-2 rounded bg-amber-50 px-1 hover:bg-amber-100">
                    <PhotoThumb src={resolveUserPhoto(student.photo, 'student')} onClick={() => setPreviewSrc(resolveUserPhoto(student.photo, 'student'))} />
                    <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-1 text-sm">
                      <input
                        type="checkbox"
                        checked={stagedSelected.includes(student.id)}
                        onChange={() => setStagedSelected((current) => toggleId(current, student.id))}
                      />
                      <span className="truncate">{studentLine(student)}</span>
                    </label>
                  </li>
                ))}
                {toClassId && !willJoin.length ? <li className="px-2 py-2 text-sm text-slate-400">None staged.</li> : null}
              </ul>
            </div>
          </section>
        </div>
        <div className="mt-6 flex justify-end">
          <Button variant="primary" onClick={openConfirm} disabled={!stagedCount || !fromClassId || !toClassId || sameClass}>
            Confirm moves
          </Button>
        </div>
      </Card>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Confirm moves"
        description={`Move ${stagedCount} student${stagedCount === 1 ? '' : 's'} from ${fromLabel} to ${toLabel}. Registration numbers do not change. Attendance history stays.`}
        confirmLabel="Confirm"
        pending={confirmMutation.isPending}
        pendingLabel="Saving…"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          if (!stagedCount) return;
          confirmMutation.mutate();
        }}
      />

      <ConfirmDialog
        isOpen={discardOpen}
        title="Discard staged moves?"
        description="You have staged moves that are not saved. Leave this page or change From class and they will be discarded."
        confirmLabel="Discard"
        cancelLabel="Stay"
        onCancel={() => {
          setDiscardOpen(false);
          setPendingFrom(null);
          setPendingHref(null);
        }}
        onConfirm={discardStaged}
      />

      <PhotoLightbox src={previewSrc} onClose={() => setPreviewSrc(null)} />
    </>
  );
}
